/**
 * ApptComms — "Booking comms" tab of the Messaging rail (APT-08, ports the
 * three-in-one's ops-booking-messages surface into GM's one messaging system).
 * Email + SMS templates keyed to appointment lifecycle triggers (confirmation,
 * reminders, cancellation, follow-up) with per-template enable, an editor with
 * variable chips, live preview against a sample booking, and SMS segment
 * count. Saves AWAIT saveApptMessageTemplate (REQ-089; seed-served in preview
 * — DataSourceStrip below). This closes the REQ-040 "confirmation to follow"
 * declared seam: the templates here ARE that flow.
 *
 * Adaptation (logged in the changelog): the source's block-based email
 * canvas renders here as body-with-variables + preview — the house messaging
 * editor pattern; block layout (logo/summary-card/buttons) is noted in the
 * template body structure and can deepen when the flow engine consumes it.
 *
 * RESTORED VERBATIM 2026-09-02 (operator ruling) after leaving the tree
 * unrecorded between 08-25 and 08-31 — see CHANGES-FROM-NOW.md. Interim
 * surface: it stays until the journey flow builder binds appointment triggers
 * per-trigger (CD reply 2026-09-02 §5/§6), because the lane it drives is live
 * and is currently the only path that sends a booking confirmation.
 *
 * Three adaptations on the restore, none of them design changes:
 *  1. the save goes through the TYPED `saveApptMessageTemplate` facade (the
 *     three appointment-comms ops exist now; the old raw `apiRaw` calls do not);
 *  2. two `Toggle label=` props became `aria-label` where a sibling element
 *     already prints the same string — the 08-19 shadow-fork sweep swapped a
 *     label-less local Toggle for the library one and left the text twice;
 *  3. the trigger reads its registered flag from the contract row
 *     (`triggerRegistered`), so a stale trigger is visible rather than silent.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Toggle, Btn, Card, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import { useT } from "../../lib/i18n";
import {
  listApptMessageTemplates, saveApptMessageTemplate,
  type ApptEmailTemplate, type ApptSmsTemplate, type ApptMessageTemplate,
} from "../appointments/appointments-api";

const VARIABLES = ["customer_name", "service", "date", "time", "location", "staff", "org", "manage_link", "book_link", "short_link", "map_link", "review_link"];
// ui-config — sample values for the template-VARIABLE preview render only
// (never data; docket 2026-07-27 item 2 confirmed as seed-data, cleared by
// applying the same disclosed-exemption marker QueueBoardBuilder/qp-pages
// already carry). The "org"/link fields were also tenant-flavoured
// ("Aperture Bank" / aperture.bk) — neutralised so this preview doesn't
// hardcode one specific tenant's name into shared authoring code.
const SAMPLE: Record<string, string> = {
  customer_name: "Jordan Alvarez", service: "Personal loan consultation", date: "Tue 28 Jul", time: "10:30",
  location: "Boston — Back Bay", staff: "Aisha Okafor", org: "Your organization",
  manage_link: "example.com/m/APT-2417", book_link: "example.com/book", short_link: "example.com/x9f2", map_link: "maps.app/example", review_link: "example.com/review/APT-2417",
};
const renderPreview = (body: string) => body.split(/(\{\{\s*[\w.]+\s*\}\})/g).map((part, i) => {
  const m = part.match(/^\{\{\s*([\w.]+)\s*\}\}$/);
  if (!m) return <span key={i}>{part}</span>;
  return <mark key={i} style={{ background: "#F4F2FF", color: "#5142C7", borderRadius: 4, padding: "0 3px", fontWeight: 600 }}>{SAMPLE[m[1]] ?? `{{${m[1]}}}`}</mark>;
});


type EditTarget = { kind: "email"; tpl: ApptEmailTemplate } | { kind: "sms"; tpl: ApptSmsTemplate };

function EditorOverlay({ target, onClose, onSaved }: { target: EditTarget; onClose: () => void; onSaved: (t: EditTarget) => void }) {
  const t = useT();
  const [tpl, setTpl] = useState<ApptEmailTemplate | ApptSmsTemplate>(target.tpl);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const isEmail = target.kind === "email";
  const email = tpl as ApptEmailTemplate;
  const sms = tpl as ApptSmsTemplate;
  const set = (patch: Record<string, unknown>) => setTpl((cur) => ({ ...cur, ...patch } as typeof cur));
  const insertVar = (v: string) => {
    const el = bodyRef.current;
    const token = `{{${v}}}`;
    if (!el) { set({ body: tpl.body + token }); return; }
    const s = el.selectionStart ?? tpl.body.length, e = el.selectionEnd ?? tpl.body.length;
    set({ body: tpl.body.slice(0, s) + token + tpl.body.slice(e) });
  };
  const smsRendered = useMemo(() => tpl.body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => SAMPLE[k] ?? k), [tpl.body]);
  const segments = Math.max(1, Math.ceil(smsRendered.length / 160));
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await saveApptMessageTemplate(target.kind, tpl as ApptMessageTemplate);
      toast({ tone: "success", title: t("appointments.comms.templateSaved", "Template saved"), subtitle: tpl.name });
      onSaved({ kind: target.kind, tpl } as EditTarget);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,18,24,0.45)", zIndex: 260, display: "flex", justifyContent: "flex-end" }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-label={tpl.name} style={{ width: "min(560px, 100vw)", height: "100%", background: "#fff", boxShadow: "-12px 0 40px rgba(15,18,24,0.18)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${T.n6}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>{tpl.name}</div>
            <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{isEmail ? t("appointments.comms.emailTemplate", "Email template") : t("appointments.comms.smsTemplate", "SMS template")} · {t("appointments.comms.trigger", "Trigger")}: {tpl.trigger}</div>
          </div>
          <Btn hierarchy="tertiary" onClick={onClose} aria-label={t("appointments.sheet.close", "Close")}><Icon.x size={16} /></Btn>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          {isEmail ? (
            <>
              <div><div style={label}>{t("appointments.comms.subject", "Subject")}</div><Input value={email.subject} onChange={(e) => set({ subject: e.target.value })} data-testid="apptcomms-subject" /></div>
              <div><div style={label}>{t("appointments.comms.preheader", "Preheader (optional)")}</div><Input value={email.preheader ?? ""} onChange={(e) => set({ preheader: e.target.value })} /></div>
            </>
          ) : null}
          <div>
            <div style={label}>{t("appointments.comms.body", "Body")}</div>
            <textarea ref={bodyRef} value={tpl.body} onChange={(e) => set({ body: e.target.value })} data-testid="apptcomms-body"
              style={{ width: "100%", minHeight: isEmail ? 180 : 110, padding: "10px 12px", border: `1px solid ${T.n5}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, lineHeight: 1.6, resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {VARIABLES.map((v) => (
                /* ui-raw: template-variable insert chip — Chip's pattern (group 6) */
                <button key={v} onClick={() => insertVar(v)} title={SAMPLE[v]}
                  style={{ padding: "3px 9px", borderRadius: 999, border: `1px solid ${T.n5}`, background: "#fff", fontSize: 11, fontFamily: "ui-monospace, Menlo, monospace", color: "#5142C7", cursor: "pointer" }}>
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
          {!isEmail ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle on={!!sms.includeShortLink} onChange={(v) => set({ includeShortLink: v })} aria-label={t("appointments.comms.includeShortLink", "Include short link")} />
              <span style={{ fontSize: 12.5, color: T.n2 }}>{t("appointments.comms.includeShortLink", "Include short link")}</span>
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: segments > 2 ? "#B45309" : T.soft, fontWeight: 600 }}>{smsRendered.length} {t("appointments.comms.chars", "chars")} · {segments} {segments === 1 ? t("appointments.comms.segment", "segment") : t("appointments.comms.segments", "segments")}</span>
            </div>
          ) : null}
          <div>
            <div style={label}>{t("appointments.comms.previewWithSampleBooking", "Preview — sample booking")}</div>
            <div style={{ border: `1px solid ${T.n6}`, borderRadius: 10, padding: "14px 16px", background: "#FAFAF8", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
              {isEmail ? <div style={{ fontWeight: 700, marginBottom: 6 }}>{renderPreview(email.subject)}</div> : null}
              {renderPreview(tpl.body)}
            </div>
          </div>
          {err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{err}</div> : null}
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.n6}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onClose}>{t("appointments.sheet.cancel", "Cancel")}</Btn>
          <Btn onClick={() => { void save(); }} disabled={saving} testid="apptcomms-save">{saving ? t("appointments.comms.saving", "Saving…") : t("appointments.comms.saveTemplate", "Save template")}</Btn>
        </div>
      </div>
    </div>
  );
}

export default function ApptComms() {
  const t = useT();
  const nav = useNav();
  const counts = useMessagingCounts();
  const [data, setData] = useState<{ email: ApptEmailTemplate[]; sms: ApptSmsTemplate[] } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditTarget | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  /* `appointments.manage` is declared in NO catalog (corrected 2026-09-08), so
     this resolved false for every persona and Booking Settings was read-only for
     everyone — including System Administrator's own operators, who could see the
     templates and change nothing. An undeclared capability is not a strict gate;
     it is a gate with no key. The enforced family for comms templates is
     `campaigns.*`, and editing a template is a write. */
  const canManage = can("campaigns.write");

  const refresh = () => listApptMessageTemplates().then((r) => { setData({ email: r.email, sms: r.sms }); setLoadErr(null); }).catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
  useEffect(() => { refresh(); }, []);

  const toggle = async (kind: "email" | "sms", tpl: ApptEmailTemplate | ApptSmsTemplate) => {
    setTogglingId(tpl.id);
    const next = { ...tpl, enabled: !tpl.enabled };
    try {
      await saveApptMessageTemplate(kind, next as ApptMessageTemplate);
      setData((cur) => cur ? { ...cur, [kind]: (cur[kind] as (ApptEmailTemplate | ApptSmsTemplate)[]).map((x) => x.id === tpl.id ? next : x) } as typeof cur : cur);
      toast({ tone: next.enabled ? "success" : "info", title: next.enabled ? t("appointments.comms.templateOn", "Template on") : t("appointments.comms.templateOff", "Template off"), subtitle: tpl.name });
    } catch (e) { toast({ tone: "danger", title: t("appointments.comms.couldntSave", "Couldn't save template"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setTogglingId(null); }
  };

  const section = (kind: "email" | "sms", title: string, rows: (ApptEmailTemplate | ApptSmsTemplate)[]) => (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "14px 18px 10px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-flex", color: "#5142C7" }}>{kind === "email" ? <Icon.mail size={15} /> : <Icon.phone size={15} />}</span>
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>{title}</span>
        <span style={{ fontSize: 11.5, color: T.soft }}>· {rows.length}</span>
      </div>
      {rows.map((tpl, i) => (
        <div key={tpl.id} data-testid={`apptcomms-row-${tpl.id}`} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "center", padding: "12px 18px", borderTop: `1px solid ${T.n6}`, opacity: tpl.enabled ? 1 : 0.62 }}>
          <Toggle on={tpl.enabled} disabled={!canManage || togglingId === tpl.id} onChange={() => { void toggle(kind, tpl); }} aria-label={tpl.name} />
          <div style={{ minWidth: 0, cursor: canManage ? "pointer" : "default" }} onClick={() => canManage && setEdit({ kind, tpl } as EditTarget)}>
            <div style={{ fontSize: 13.5, fontWeight: 650 }}>{tpl.name}</div>
            <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t("appointments.comms.trigger", "Trigger")}: {tpl.trigger}{tpl.triggerRegistered === false ? ` — ${t("appointments.comms.triggerUnregistered", "not a registered trigger; this template never fires")}` : ""}{kind === "email" ? ` · ${(tpl as ApptEmailTemplate).subject}` : ""}
            </div>
          </div>
          {canManage ? <Btn variant="secondary" size="sm" onClick={() => setEdit({ kind, tpl } as EditTarget)} testid={`apptcomms-edit-${tpl.id}`}><Icon.edit size={13} /> {t("appointments.comms.edit", "Edit")}</Btn> : null}
        </div>
      ))}
    </Card>
  );

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "apptcomms", counts)} activeTab="apptcomms">
      <PageHeader
        breadcrumb={null}
        title={t("appointments.comms.bookingComms", "Booking comms")}
        subtitle={t("appointments.comms.subtitle", "The messages customers get around an appointment — confirmations, reminders, cancellations and follow-ups, on email and SMS.")}
      />
      {/* The interim label lives IN the surface, not only in release notes (CD
          reply 2026-09-02 §1) — and it names the CONDITION, not a date: this
          screen retires when the journey flow builder can bind a message per
          appointment trigger. Absorbed upstream so it stops being a local edit
          in CC's mount (their 2026-09-03 addendum). */}
      <div style={{ margin: "0 24px 4px", padding: "9px 12px", display: "flex", gap: 8, alignItems: "flex-start", maxWidth: 860, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12, color: T.n2, lineHeight: 1.45 }}>
        <span style={{ color: T.p1, flexShrink: 0, display: "inline-flex", marginTop: 1 }}><Icon.info size={13} /></span>
        <span>{t("appointments.comms.interimLabel", "Interim surface — these templates move into the journey flow builder once it can bind a message per appointment trigger.")}</span>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 860 }}>
        {loadErr ? (
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "#FDECEC", border: "1px solid #F5B5B5", color: "#8A1F1F", fontSize: 13 }}>
            {t("appointments.comms.loadFailed", "Couldn't load booking comms templates")} — {loadErr} <Btn size="sm" variant="secondary" onClick={refresh} style={{ marginLeft: 8 }}>{t("appointments.book.retry", "Retry")}</Btn>
          </div>
        ) : !data ? (
          <div style={{ fontSize: 13, color: T.soft }}>{t("appointments.comms.loading", "Loading templates…")}</div>
        ) : (
          <>
            {section("email", t("appointments.comms.emailTemplates", "Email templates"), data.email)}
            {section("sms", t("appointments.comms.smsTemplates", "SMS templates"), data.sms)}
            <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.6 }}>
              {t("appointments.comms.seamNote", "These templates are the standard appointment confirmation flow: every booking surface (day book, concierge, customer 360, journeys) hands off to them at status “booked”.")}
            </div>
          </>
        )}
        <DataSourceStrip screen="apptcomms" ops={["listApptMessageTemplates", "saveApptMessageTemplate"]} />
      </div>
      {edit ? <EditorOverlay target={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} /> : null}
    </Screen>
  );
}
